import {useAuthState} from "react-firebase-hooks/auth";

import {type SettingsDoc, SettingsDocSchema} from "@repo/shared";
import type {UseQueryResult} from "@tanstack/react-query";

import {auth} from "~/firebase/firebase";

import {BaseCollectionConfig} from "../BaseCollectionConfig";
import {collectionNameV1} from "../collectionName";
import {getByIdHookFactory} from "../factories/getByIdHookFactory";
import {getByIdRealtimeSubFactory} from "../factories/getByIdRealtimeSubFactory";
import {mutationHookFactory} from "../factories/mutationHookFactory";

export class SettingsCollectionConfig extends BaseCollectionConfig<
  typeof SettingsDocSchema,
  unknown
> {
  constructor() {
    super(SettingsDocSchema);
  }

  getCollection(): [string, ...(string | undefined)[]] {
    return [collectionNameV1("settings")];
  }

  parser(id: string, data: any): SettingsDoc {
    if (data === undefined)
      return {
        id: id,
        showFps: false,
        liveReplays: true,
        showPlayerNames: false,
        showTrails: true,
        showPersonalBestGhost: true,
        musicVolume: 1.0,
        soundVolume: 1.0,
        enableLogViewer: false,
      };
    return {
      id,
      showFps: data.showFps ?? false,
      liveReplays: data.liveReplays ?? true,
      showPlayerNames: data.showPlayerNames ?? false,
      showTrails: data.showTrails ?? true,
      showPersonalBestGhost: data.showPersonalBestGhost ?? true,
      musicVolume: data.musicVolume ?? 1.0,
      soundVolume: data.soundVolume ?? 1.0,
      enableLogViewer: data.enableLogViewer ?? false,
    };
  }
}

const config = new SettingsCollectionConfig();
export const useSettings = getByIdHookFactory(config);
export const useSettingsRealtimeSub = getByIdRealtimeSubFactory(config);
export const useUpdateSettings = mutationHookFactory(config);

/**
 * Hook that retrieves the current user's settings
 * @returns Query result containing the user's settings
 */
export const useCurrentUserSettings = (): UseQueryResult<SettingsDoc> => {
  const [user] = useAuthState(auth);
  return useSettings({id: user?.uid}, {waitForRealtimeUpdates: true});
};

/**
 * Hook that retrieves the current user's settings and sets up a realtime subscription
 * @returns Query result containing the user's settings with realtime updates
 */
export const useCurrentUserSettingsRealtimeSub = () => {
  const [user] = useAuthState(auth);

  // Set up the realtime subscription
  useSettingsRealtimeSub({id: user?.uid});
};
